import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "./SideBar";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faArrowDown,
  faTrophy,
  faUser,
  faUsers,
  faCheckCircle,
  faSpinner,
  faChevronRight,
  faSearch,
  faPlus,
  faTimes,
  faLink,
  faGlobe,
  faFolder,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL_INFLUENCER;
const ADD_INFLUENCER_API = `${API_URL}/influencers/add`;

/* ── Project tag color palette ─────────────────────────────────── */
const PROJECT_COLORS = [
  { bg: "#E3F2FD", color: "#1565C0", border: "#90CAF9" },
  { bg: "#E8F5E9", color: "#2E7D32", border: "#A5D6A7" },
  { bg: "#FFF3E0", color: "#E65100", border: "#FFCC80" },
  { bg: "#F3E5F5", color: "#7B1FA2", border: "#CE93D8" },
  { bg: "#FFF8E1", color: "#F57F17", border: "#FFE082" },
  { bg: "#E0F7FA", color: "#00695C", border: "#80DEEA" },
  { bg: "#FCE4EC", color: "#C62828", border: "#EF9A9A" },
  { bg: "#F1F8E9", color: "#558B2F", border: "#C5E1A5" },
  { bg: "#EDE7F6", color: "#4527A0", border: "#B39DDB" },
  { bg: "#E0F2F1", color: "#00695C", border: "#80CBC4" },
];

const getProjectColor = (project) => {
  let hash = 0;
  for (let i = 0; i < project.length; i++) {
    hash = project.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length];
};

const InfluencerLeaderboard = () => {
  const navigate = useNavigate();

  // ── Search state (client-side instant filter) ────────────────────
  const [searchQuery, setSearchQuery] = useState("");

  // ── Leaderboard state ────────────────────────────────────────────
  const [type, setType] = useState("top");
  const [limit, setLimit] = useState(5);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Project filter state ─────────────────────────────────────────
  const [activeProjectFilter, setActiveProjectFilter] = useState("All");
  const [allProjects, setAllProjects] = useState([]);

  // ── Add Influencer modal state ───────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [xLink, setXLink] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");

  // ✅ CHANGED: projects input is now a comma-separated text field
  const [addProjectsText, setAddProjectsText] = useState("");

  // ── Parse comma-separated projects → array (lowercase, unique) ───
  const parseProjectsInput = (text) => {
    if (!text) return [];
    const arr = String(text)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => s.toLowerCase());
    return Array.from(new Set(arr));
  };

  // ── Extract username helper ──────────────────────────────────────
  const extractUsernameFromInput = (input) => {
    const raw = String(input || "").trim();
    if (!raw) return "";
    if (raw.startsWith("@")) return raw.slice(1).trim();
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      try {
        const u = new URL(raw);
        const host = (u.hostname || "").toLowerCase();
        const allowed =
          host === "x.com" ||
          host.endsWith(".x.com") ||
          host === "twitter.com" ||
          host.endsWith(".twitter.com") ||
          host === "mobile.twitter.com";
        if (!allowed) return "";
        const parts = u.pathname.split("/").filter(Boolean);
        return (parts[0] || "").trim();
      } catch (e) {
        return "";
      }
    }
    return raw;
  };

  const isValidUsername = (username) => /^[A-Za-z0-9_]{1,15}$/.test(username);

  // ── Fetch leaderboard ────────────────────────────────────────────
  const fetchInfluencers = async (selectedType, selectedLimit) => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `${API_URL}/influencers?type=${selectedType}&limit=${selectedLimit}`
      );

      if (res.data?.status === "success") {
        const data = res.data.data || [];
        setInfluencers(data);

        // Extract unique projects
        const projectSet = new Set();
        data.forEach((inf) => {
          if (inf.projects && Array.isArray(inf.projects)) {
            inf.projects.forEach((p) => projectSet.add(p));
          }
        });
        setAllProjects([...projectSet].sort());
        setActiveProjectFilter("All");
      } else {
        setError("Failed to load influencers.");
        setInfluencers([]);
      }
    } catch (err) {
      console.error("Error fetching influencers:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to fetch influencers."
      );
      setInfluencers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfluencers(type, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, limit]);

  // ── Client-side filtering (search + project) ─────────────────────
  const filteredInfluencers = influencers.filter((inf) => {
    // Project filter
    if (activeProjectFilter !== "All") {
      if (
        !inf.projects ||
        !Array.isArray(inf.projects) ||
        !inf.projects.includes(activeProjectFilter)
      ) {
        return false;
      }
    }

    // Search filter — matches name, username, category, projects
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const nameMatch = (inf.name || "").toLowerCase().includes(q);
      const usernameMatch = (inf.username || "").toLowerCase().includes(q);
      const categoryMatch = (inf.category || "").toLowerCase().includes(q);
      const projectMatch =
        inf.projects &&
        Array.isArray(inf.projects) &&
        inf.projects.some((p) => String(p).toLowerCase().includes(q));
      return nameMatch || usernameMatch || categoryMatch || projectMatch;
    }

    return true;
  });

  // ── Add Influencer submit ────────────────────────────────────────
  const handleAddInfluencer = async (e) => {
    e.preventDefault();
    setAddError("");
    setAddSuccess("");

    const username = extractUsernameFromInput(xLink);

    if (!username) {
      setAddError("Please paste a valid X/Twitter profile link or username.");
      return;
    }
    if (!isValidUsername(username)) {
      setAddError(
        "Invalid username. Only letters, numbers, underscore (max 15 chars)."
      );
      return;
    }

    const projects = parseProjectsInput(addProjectsText);

    setAddLoading(true);
    try {
      const res = await axios.post(
        ADD_INFLUENCER_API,
        { username, projects },
        { headers: { "Content-Type": "application/json" } }
      );

      const ok =
        res?.data?.status === "success" ||
        res?.data?.success === true ||
        res?.status === 200 ||
        res?.status === 201;

      if (ok) {
        setAddSuccess(`Added @${username} to influencer list.`);
        await fetchInfluencers(type, limit);
        setTimeout(() => {
          setShowAddModal(false);
          setXLink("");
          setAddProjectsText("");
          setAddSuccess("");
          setAddError("");
        }, 700);
      } else {
        setAddError(res?.data?.message || "Failed to add influencer.");
      }
    } catch (err) {
      console.error("Add influencer error:", err);
      setAddError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to add influencer. Please try again."
      );
    } finally {
      setAddLoading(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────
  const formatNumber = (num) => {
    if (!num && num !== 0) return "—";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
    return num.toLocaleString();
  };

  const getRankColor = (score) => {
    if (score >= 80) return "#28A745";
    if (score >= 60) return "#FFC107";
    if (score >= 40) return "#FF8C00";
    return "#DC3545";
  };

  const getRankBadgeStyle = (index) => {
    if (type === "top") {
      if (index === 0) return { bg: "#FFD700", color: "#7A5C00" };
      if (index === 1) return { bg: "#C0C0C0", color: "#555" };
      if (index === 2) return { bg: "#CD7F32", color: "#fff" };
    }
    return { bg: "var(--background-light)", color: "var(--text-gray)" };
  };

  const limitOptions = [5, 10, 20, 50];

  // ── Project Tag ──────────────────────────────────────────────────
  const ProjectTag = ({ project }) => {
    const colors = getProjectColor(project);
    return (
      <span
        style={{
          backgroundColor: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
          padding: "2px 10px",
          borderRadius: 12,
          fontSize: "0.7rem",
          fontWeight: 600,
          textTransform: "capitalize",
          whiteSpace: "nowrap",
          lineHeight: "1.6",
        }}
      >
        {project}
      </span>
    );
  };

  // ── Influencer Row ───────────────────────────────────────────────
  const InfluencerRow = ({ influencer, index }) => {
    const badge = getRankBadgeStyle(index);
    const projects = influencer.projects || [];

    return (
      <div
        className="card"
        style={{
          padding: "16px 20px",
          marginBottom: 12,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 16,
          borderLeft: `4px solid ${getRankColor(influencer.ranking_score)}`,
          transition: "all 0.2s ease",
        }}
        onClick={() =>
          navigate(`/influencer-profiles?username=${influencer.username}`)
        }
      >
        {/* Rank */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: badge.bg,
            color: badge.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "0.95rem",
            flexShrink: 0,
          }}
        >
          {index + 1}
        </div>

        {/* Profile Picture */}
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
            backgroundColor: "var(--background-light)",
            border: "2px solid var(--border-color)",
          }}
        >
          {influencer.profile_picture ? (
            <img
              src={String(influencer.profile_picture).replace(
                "_normal",
                "_400x400"
              )}
              alt={influencer.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faUser}
                style={{ color: "var(--text-gray)" }}
              />
            </div>
          )}
        </div>

        {/* Name + Username + Category + Projects */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "var(--text-dark)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {influencer.name || influencer.username}
            </span>
            {influencer.is_blue_verified && (
              <FontAwesomeIcon
                icon={faCheckCircle}
                style={{ color: "#1DA1F2", fontSize: "0.85rem" }}
              />
            )}
            {influencer.category && (
              <span
                style={{
                  backgroundColor: "var(--primary-color)",
                  color: "#fff",
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {influencer.category}
              </span>
            )}
          </div>

          {/* Username + Project Tags */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: "var(--text-gray)", fontSize: "0.85rem" }}>
              @{influencer.username}
            </span>
            {projects.length > 0 &&
              projects.map((project) => (
                <ProjectTag key={project} project={project} />
              ))}
          </div>
        </div>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            gap: 20,
            alignItems: "center",
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <div style={{ textAlign: "center", minWidth: 60 }}>
            <div
              style={{
                fontSize: "0.7rem",
                color: "var(--text-gray)",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Followers
            </div>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--text-dark)",
              }}
            >
              {formatNumber(influencer.followers)}
            </div>
          </div>

          <div style={{ textAlign: "center", minWidth: 60 }}>
            <div
              style={{
                fontSize: "0.7rem",
                color: "var(--text-gray)",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Tweets
            </div>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--text-dark)",
              }}
            >
              {formatNumber(influencer.tweets_count)}
            </div>
          </div>

          <FontAwesomeIcon
            icon={faChevronRight}
            style={{ color: "var(--text-gray)", fontSize: "0.85rem" }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        {/* ── Header ───────────────────────────────────────────── */}
        <div
          className="dashboard-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 className="dashboard-title">
              <FontAwesomeIcon
                icon={faTrophy}
                style={{ marginRight: 10, color: "var(--primary-color)" }}
              />
              Influencer Leaderboard
            </h1>
            <p className="dashboard-subtitle">
              Search influencers or browse ranked lists by performance score.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="btn btn-outline"
              onClick={() => navigate("/general-tweets")}
              style={{
                height: 44,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 8,
                whiteSpace: "nowrap",
                borderColor: "var(--primary-color)",
                color: "var(--primary-color)",
                fontWeight: 600,
              }}
            >
              <FontAwesomeIcon icon={faGlobe} />
              General Tweets
            </button>

            <button
              className="btn btn-primary"
              onClick={() => {
                setShowAddModal(true);
                setXLink("");
                setAddError("");
                setAddSuccess("");
                setAddProjectsText("");
              }}
              style={{
                height: 44,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 8,
                whiteSpace: "nowrap",
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Influencer
            </button>
          </div>
        </div>

        {/* ── Search Bar (instant client-side filter) ──────────── */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ position: "relative" }}>
            <FontAwesomeIcon
              icon={faSearch}
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-gray)",
                fontSize: "0.9rem",
              }}
            />
            <input
              type="text"
              placeholder="Filter by name, username, category, or project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                paddingLeft: 42,
                paddingRight: searchQuery ? 42 : 14,
                height: 46,
                fontSize: "0.95rem",
                borderRadius: 10,
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  color: "var(--text-gray)",
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Clear"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>

        {/* ── Controls: Type Toggle + Limit ────────────────────── */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setType("top")}
                style={{
                  padding: "8px 20px",
                  borderRadius: 20,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  backgroundColor:
                    type === "top" ? "#28A745" : "var(--background-light)",
                  color: type === "top" ? "#fff" : "var(--text-gray)",
                  transition: "all 0.2s ease",
                }}
              >
                <FontAwesomeIcon icon={faArrowUp} />
                Top Ranked
              </button>
              <button
                onClick={() => setType("bottom")}
                style={{
                  padding: "8px 20px",
                  borderRadius: 20,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  backgroundColor:
                    type === "bottom" ? "#DC3545" : "var(--background-light)",
                  color: type === "bottom" ? "#fff" : "var(--text-gray)",
                  transition: "all 0.2s ease",
                }}
              >
                <FontAwesomeIcon icon={faArrowDown} />
                Bottom Ranked
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: "0.85rem",
                  color: "var(--text-gray)",
                  fontWeight: 600,
                }}
              >
                Show:
              </span>
              {limitOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setLimit(opt)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    border: `1.5px solid ${
                      limit === opt ? "var(--primary-color)" : "var(--border-color)"
                    }`,
                    backgroundColor:
                      limit === opt ? "var(--primary-color)" : "transparent",
                    color: limit === opt ? "#fff" : "var(--text-gray)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Project Filter Chips ─────────────────────────────── */}
        {allProjects.length > 0 && !loading && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 20,
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faFolder}
              style={{
                color: "var(--text-gray)",
                fontSize: "0.85rem",
                marginRight: 4,
              }}
            />
            <span
              style={{
                fontSize: "0.82rem",
                color: "var(--text-gray)",
                fontWeight: 600,
                marginRight: 4,
              }}
            >
              Projects:
            </span>

            <button
              onClick={() => setActiveProjectFilter("All")}
              style={{
                padding: "5px 14px",
                borderRadius: 16,
                fontSize: "0.78rem",
                fontWeight: 600,
                border: `1.5px solid ${
                  activeProjectFilter === "All" ? "#333" : "#CCC"
                }`,
                backgroundColor: activeProjectFilter === "All" ? "#333" : "#F5F5F5",
                color: activeProjectFilter === "All" ? "#fff" : "#333",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              All
            </button>

            {allProjects.map((project) => {
              const isActive = activeProjectFilter === project;
              const colors = getProjectColor(project);
              return (
                <button
                  key={project}
                  onClick={() => setActiveProjectFilter(project)}
                  style={{
                    padding: "5px 14px",
                    borderRadius: 16,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    border: `1.5px solid ${
                      isActive ? colors.color : colors.border
                    }`,
                    backgroundColor: isActive ? colors.color : colors.bg,
                    color: isActive ? "#fff" : colors.color,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    textTransform: "capitalize",
                  }}
                >
                  {project}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────── */}
        {error && <div className="alert alert-error">{error}</div>}

        {/* ── Loading ──────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div className="spinner" />
            <p style={{ color: "var(--text-gray)", marginTop: 8 }}>
              Loading {type} influencers...
            </p>
          </div>
        )}

        {/* ── Results Header ───────────────────────────────────── */}
        {!loading && filteredInfluencers.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              padding: "0 4px",
            }}
          >
            <span
              style={{
                fontSize: "0.9rem",
                color: "var(--text-gray)",
                fontWeight: 600,
              }}
            >
              <FontAwesomeIcon icon={faUsers} style={{ marginRight: 6 }} />
              Showing {filteredInfluencers.length}{" "}
              {type === "top" ? "top" : "bottom"} influencer
              {filteredInfluencers.length !== 1 ? "s" : ""}
              {activeProjectFilter !== "All" && (
                <span>
                  {" "}
                  in{" "}
                  <span
                    style={{
                      color: "var(--primary-color)",
                      textTransform: "capitalize",
                    }}
                  >
                    {activeProjectFilter}
                  </span>
                </span>
              )}
              {searchQuery.trim() && (
                <span> matching "{searchQuery.trim()}"</span>
              )}
            </span>
          </div>
        )}

        {/* ── Influencer List ──────────────────────────────────── */}
        {!loading &&
          filteredInfluencers.map((influencer, index) => (
            <InfluencerRow
              key={influencer.username || index}
              influencer={influencer}
              index={index}
            />
          ))}

        {/* ── Empty State ──────────────────────────────────────── */}
        {!loading && filteredInfluencers.length === 0 && !error && (
          <div className="card" style={{ textAlign: "center", padding: 48 }}>
            <FontAwesomeIcon
              icon={faUsers}
              style={{
                fontSize: "2.5rem",
                color: "var(--border-color)",
                marginBottom: 16,
              }}
            />
            <h3 style={{ color: "var(--text-gray)", fontWeight: 600 }}>
              {searchQuery.trim() || activeProjectFilter !== "All"
                ? "No matching influencers"
                : "No influencers found"}
            </h3>
            <p style={{ color: "var(--text-gray)", fontSize: "0.9rem" }}>
              {searchQuery.trim()
                ? `No results for "${searchQuery.trim()}". Try a different search.`
                : activeProjectFilter !== "All"
                ? "Try selecting a different project or 'All'."
                : "Try adjusting the filters above."}
            </p>
          </div>
        )}

        {/* ── Add Influencer Modal ─────────────────────────────── */}
        {showAddModal && (
          <div
            onClick={() => !addLoading && setShowAddModal(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: 16,
            }}
          >
            <div
              className="card"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 520,
                borderRadius: 14,
                boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
                padding: 18,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <h3 style={{ margin: 0, color: "var(--text-dark)" }}>
                    <FontAwesomeIcon
                      icon={faPlus}
                      style={{ marginRight: 8, color: "var(--primary-color)" }}
                    />
                    Add Influencer
                  </h3>
                  <p
                    style={{
                      margin: "6px 0 0",
                      color: "var(--text-gray)",
                      fontSize: "0.9rem",
                    }}
                  >
                    Paste X/Twitter profile link (or username). We'll extract the
                    handle and add it.
                  </p>
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setShowAddModal(false)}
                  disabled={addLoading}
                  style={{ borderRadius: 10 }}
                  title="Close"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleAddInfluencer} style={{ marginTop: 14 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      backgroundColor: "var(--background-light)",
                      borderRadius: 12,
                      padding: "10px 12px",
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLink}
                      style={{ color: "var(--text-gray)" }}
                    />
                    <input
                      value={xLink}
                      onChange={(e) => {
                        setXLink(e.target.value);
                        if (addError) setAddError("");
                      }}
                      placeholder="e.g. https://x.com/uttekar_shreyas"
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: "0.95rem",
                        color: "var(--text-dark)",
                      }}
                      disabled={addLoading}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={addLoading || !String(xLink || "").trim()}
                    style={{
                      borderRadius: 12,
                      padding: "10px 14px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {addLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Adding...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPlus} /> Add
                      </>
                    )}
                  </button>
                </div>

                {addError && (
                  <div className="alert alert-error" style={{ marginTop: 12 }}>
                    {addError}
                  </div>
                )}
                {addSuccess && (
                  <div className="alert alert-success" style={{ marginTop: 12 }}>
                    {addSuccess}
                  </div>
                )}

                {/* ✅ Projects Input (comma-separated) */}
                <div style={{ marginTop: 14 }}>
                  <label
                    style={{
                      fontSize: "0.82rem",
                      color: "var(--text-gray)",
                      fontWeight: 600,
                      marginBottom: 8,
                      display: "block",
                    }}
                  >
                    <FontAwesomeIcon icon={faFolder} style={{ marginRight: 6 }} />
                    Projects (optional)
                  </label>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      backgroundColor: "var(--background-light)",
                      borderRadius: 12,
                      padding: "10px 12px",
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faFolder}
                      style={{ color: "var(--text-gray)" }}
                    />
                    <input
                      value={addProjectsText}
                      onChange={(e) => {
                        setAddProjectsText(e.target.value);
                        if (addError) setAddError("");
                      }}
                      placeholder="e.g. sports, football"
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: "0.95rem",
                        color: "var(--text-dark)",
                      }}
                      disabled={addLoading}
                    />
                  </div>

                  {/* Preview parsed tags */}
                  {parseProjectsInput(addProjectsText).length > 0 && (
                    <div
                      style={{
                        marginTop: 10,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      {parseProjectsInput(addProjectsText).map((p) => (
                        <ProjectTag key={p} project={p} />
                      ))}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: 8,
                      fontSize: "0.78rem",
                      color: "var(--text-gray)",
                    }}
                  >
                    Use commas for multiple projects. Saved as an array like:{" "}
                    <b>["sports", "football"]</b>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    fontSize: "0.85rem",
                    color: "var(--text-gray)",
                  }}
                >
                  Accepted formats: <b>x.com</b>, <b>twitter.com</b>,{" "}
                  <b>@username</b>, <b>username</b>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InfluencerLeaderboard;